/**
 * components/procurement/ScenarioHistoryDialog.jsx — view & roll back a
 * scenario's assumption changes (every edit / AI / Beacon change is snapshotted).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2, RotateCcw } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { getScenarioRevisions, restoreScenarioRevision } from '../../api/budgets';

const fmt = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

export function ScenarioHistoryDialog({ open, onOpenChange, accountId, budgetId, scenarioId, scenarioName, onRestored }) {
  const [revs, setRevs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!open || !scenarioId) return;
    setLoading(true);
    getScenarioRevisions({ accountId, id: budgetId, scenarioId })
      .then((d) => setRevs(d.revisions || []))
      .catch(() => setRevs([]))
      .finally(() => setLoading(false));
  }, [open, accountId, budgetId, scenarioId]);

  const restore = async (rev) => {
    setRestoring(rev.id);
    try {
      await restoreScenarioRevision({ accountId, id: budgetId, scenarioId, revId: rev.id });
      toast.success('Scenario rolled back', { description: 'Restored a previous version.' });
      onOpenChange(false);
      onRestored?.();
    } catch (e) {
      toast.error('Could not roll back', { description: e?.response?.data?.detail || e.message });
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="scenario-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-600" /> History — {scenarioName}
          </DialogTitle>
          <DialogDescription>
            Each change is snapshotted. Restore any previous version — the restore itself is undoable.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading history…
          </div>
        ) : revs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No changes recorded yet.</div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {revs.map((r) => (
              <div key={r.id} className="flex items-start justify-between border rounded-md px-3 py-2" data-testid={`revision-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{r.summary || 'Base assumptions'}</div>
                  <div className="text-xs text-muted-foreground">{fmt(r.created_at)} · {r.actor || '—'} · {r.reason}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(r)} disabled={!!restoring} data-testid={`revision-restore-${r.id}`}>
                  {restoring === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
